import { ADDRESS_STATE_PROPS, PROPERTY_USAGE_TYPE_ENUM, UNIT_COUNT_MAP, LANIS_TO_GSE_PROPERTY_TYPE } from "../constants";
import { PURCHASE } from "../../constants/document_types";
import {
  PUD,
  CONDOMINIUM,
  COOPERATIVE,
  DETACHED_CONDOMINIUM,
  HIGH_RISE_CONDOMINIUM,
  MANUFACTURED_CONDO_PUD_COOP,
  MANUFACTURED_SINGLE_WIDE,
  MANUFACTURED_DOUBLE_WIDE,
  MANUFACTURED_MULTI_WIDE,
  MODULAR_PRE_FABRICATED_HOME
} from "../../constants/property_type";

// LE Price Eng (Edit Loan) stores property type on the loan; fall back to the
// property record. Used by both the property detail and the PROJECT exporters.
function resolvePropertyType(document) {
  const loanIndex = document.summary && document.summary.selection_loan_number
    ? parseInt(document.summary.selection_loan_number, 10) - 1
    : -1;
  const selectedLoan = loanIndex >= 0 && document.loans ? document.loans[loanIndex] : null;
  return (selectedLoan && selectedLoan.property_type) || document.property.property_type;
}

const MANUFACTURED_TYPES = [
  MANUFACTURED_SINGLE_WIDE,
  MANUFACTURED_DOUBLE_WIDE,
  MANUFACTURED_MULTI_WIDE,
  MANUFACTURED_CONDO_PUD_COOP
];

// MISMO ConstructionMethodType enum.
function constructionMethodType(property_type) {
  if (property_type === MODULAR_PRE_FABRICATED_HOME) return "Modular";
  if (MANUFACTURED_TYPES.includes(property_type)) return "Manufactured";
  return "SiteBuilt";
}

// MISMO ProjectLegalStructureType enum. Only set when the property is in a
// project; POINT reads this for its Property Type field.
const PROJECT_LEGAL_STRUCTURE = {
  [PUD]: "PlannedUnitDevelopment",
  [CONDOMINIUM]: "Condominium",
  [DETACHED_CONDOMINIUM]: "Condominium",
  [HIGH_RISE_CONDOMINIUM]: "Condominium",
  [MANUFACTURED_CONDO_PUD_COOP]: "Condominium",
  [COOPERATIVE]: "Cooperative"
};

export function populatePropertyAddress(builder, { document }) {
  const { address, address2, city, state, zip } = document.property;
  if (address) builder.ele("AddressLineText").txt(address);
  if (address2) builder.ele("AddressAdditionalLineText").txt(address2);
  if (city) builder.ele("CityName").txt(city);
  if (zip) builder.ele("PostalCode").txt(zip);
  if (state) {
    const stateTag = state.length === 2 ? ADDRESS_STATE_PROPS.state_code : ADDRESS_STATE_PROPS.state_name;
    builder.ele(stateTag).txt(state);
  }
}

export function skipProject({ document }) {
  return !PROJECT_LEGAL_STRUCTURE[resolvePropertyType(document)];
}

export function populateProjectDetail(builder, { document }) {
  const structure = PROJECT_LEGAL_STRUCTURE[resolvePropertyType(document)];
  if (structure) {
    builder.ele("ProjectLegalStructureType").txt(structure);
  }
}

export function populatePropertyDetail(builder, { document }) {
  const { classification, attachment_type } = document.property;
  const property_type = resolvePropertyType(document);

  // Alphabetical order required by MISMO 3.4 XSD
  if (attachment_type) {
    builder.ele("AttachmentType").txt(attachment_type);
  }
  builder.ele("CommunityPropertyStateIndicator").txt("false");
  builder.ele("ConstructionMethodType").txt(constructionMethodType(property_type));
  builder.ele("ConstructionStatusType").txt("ExistingConstruction");
  builder.ele("FHASecondaryResidenceIndicator").txt("false");

  const unitCount = UNIT_COUNT_MAP[property_type];
  if (unitCount) {
    builder.ele("FinancedUnitCount").txt(unitCount);
  }

  // Plain GSEPropertyType: LANIS's own importer reads this, and POINT tolerates
  // (ignores) it. The ULAD-extension copy below is what POINT actually reads for
  // its Property Type dropdown. Emitting both keeps the LANIS round-trip working.
  const gsePropertyType = LANIS_TO_GSE_PROPERTY_TYPE[property_type];
  if (gsePropertyType) {
    builder.ele("GSEPropertyType").txt(gsePropertyType);
  }

  builder.ele("PropertyEstateType").txt("FeeSimple");

  const listedPrice = parseFloat(document.property.listed_price);
  if (listedPrice > 0) {
    builder.ele("PropertyEstimatedValueAmount").txt(String(listedPrice));
  }

  builder.ele("PropertyExistingCleanEnergyLienIndicator").txt("false");

  const isInProject = [
    PUD,
    CONDOMINIUM,
    COOPERATIVE,
    DETACHED_CONDOMINIUM,
    HIGH_RISE_CONDOMINIUM,
    MANUFACTURED_CONDO_PUD_COOP
  ].includes(property_type);
  builder.ele("PropertyInProjectIndicator").txt(String(isInProject));

  const usageType = PROPERTY_USAGE_TYPE_ENUM[classification];
  if (usageType) {
    builder.ele("PropertyUsageType").txt(usageType);
  }

  builder.ele("PUDIndicator").txt(String(property_type === PUD));

  // GSEPropertyType is a ULAD (URLA) EXTENSION element, not a base PROPERTY_DETAIL
  // child. POINT (Calyx, DU/ULAD spec) reads its Property Type dropdown from the
  // ULAD-namespaced version and ignores a plain <GSEPropertyType> (its own export
  // drops it), so Property Type came in blank. Emit it in the ULAD extension.
  // EXTENSION is the last child of PROPERTY_DETAIL per the MISMO XSD.
  if (gsePropertyType) {
    builder
      .ele("EXTENSION")
      .ele("OTHER")
      .ele("ULAD:PROPERTY_DETAIL_EXTENSION")
      .ele("ULAD:GSEPropertyType")
      .txt(gsePropertyType);
  }
}

// Subject property appraised/market value. POINT reads its "Appr Value" field
// from SUBJECT_PROPERTY/PROPERTY_VALUATIONS/PROPERTY_VALUATION/
// PROPERTY_VALUATION_DETAIL/PropertyValuationAmount (confirmed from a POINT
// export). The App's Market Value = property.listed_price (also exported as
// PropertyEstimatedValueAmount = POINT's Estimated Value), so both fields fill.
export function skipPropertyValuation({ document }) {
  return !(parseFloat(document.property.listed_price) > 0);
}

export function populatePropertyValuation(builder, { document }) {
  const value = parseFloat(document.property.listed_price);
  if (value > 0) {
    builder.ele("PropertyValuationAmount").txt(String(value));
  }
}

export function skipSalesContracts({ options }) {
  return !options || options.documentType !== PURCHASE;
}

export function populateSalesContract(builder, { document }) {
  const listedPrice = parseFloat(document.property.listed_price);
  if (listedPrice > 0) {
    builder.ele("SALES_CONTRACT_DETAIL")
      .ele("SalesContractAmount").txt(String(listedPrice));
  }
}
